import React, { useState, memo } from 'react'
import { CommonTextField, CommonTextarea } from '@/components/common'
import {
  SubSection,
  StyledDemoGrid,
  DemoComponent,
  StyledSizeLabel,
} from '../styled.jsx'

export const TextFieldSection = () => {
  // Basic TextField states - each demo gets its own state
  const [standardValue, setStandardValue] = useState('')
  const [placeholderValue, setPlaceholderValue] = useState('')
  const [requiredValue, setRequiredValue] = useState('')
  const [errorValue, setErrorValue] = useState('')
  const [debouncedValue, setDebouncedValue] = useState('')
  const [restrictedValue, setRestrictedValue] = useState('')
  const [readonlyValue, setReadonlyValue] = useState('Read-only content')
  const [loadingValue, setLoadingValue] = useState('')

  // State demo values
  const [enabledValue, setEnabledValue] = useState('')
  const [helperValue, setHelperValue] = useState('')

  // Size demo values
  const [smallValue, setSmallValue] = useState('')
  const [smallMultiValue, setSmallMultiValue] = useState('')
  const [mediumValue, setMediumValue] = useState('')
  const [mediumMultiValue, setMediumMultiValue] = useState('')

  // Input type demo values
  const [emailValue, setEmailValue] = useState('')
  const [passwordValue, setPasswordValue] = useState('')
  const [numberValue, setNumberValue] = useState('')

  // Textarea state
  const [textareaValue, setTextareaValue] = useState('')
  const [textareaRequired, setTextareaRequired] = useState('')
  const [textareaError, setTextareaError] = useState('')
  const [textareaDebounced, setTextareaDebounced] = useState('')
  const [textareaRestricted, setTextareaRestricted] = useState('')

  return (
    <>
      {/* Text Field Variants */}
      <SubSection
        title='Text Field Variants'
        description='Single-line text input fields with different configurations'
      >
        <StyledSizeLabel variant='h6' gutterBottom>
          Basic Fields
        </StyledSizeLabel>
        <StyledDemoGrid>
          <DemoComponent
            label='Standard'
            component={
              <CommonTextField
                id='demo-standard'
                label='Standard Field'
                value={standardValue}
                onChange={setStandardValue}
              />
            }
          />
          <DemoComponent
            label='With Placeholder'
            component={
              <CommonTextField
                id='demo-placeholder'
                label='With Placeholder'
                value={placeholderValue}
                onChange={setPlaceholderValue}
                placeholder='Enter text here...'
              />
            }
          />
        </StyledDemoGrid>
      </SubSection>

      {/* Text Field Features */}
      <SubSection
        title='Text Field Features'
        description='Advanced features like validation, debouncing, and restricted input'
      >
        <StyledSizeLabel variant='h6' gutterBottom>
          With Features
        </StyledSizeLabel>
        <StyledDemoGrid>
          <DemoComponent
            label='Required Field'
            component={
              <CommonTextField
                id='demo-required'
                label='Required Field'
                value={requiredValue}
                onChange={setRequiredValue}
                required={true}
                helperText='This field is required'
              />
            }
          />
          <DemoComponent
            label='With Error'
            component={
              <CommonTextField
                id='demo-error'
                label='Field with Error'
                value={errorValue}
                onChange={setErrorValue}
                error='Please enter valid text'
              />
            }
          />
          <DemoComponent
            label='Debounced Input'
            component={
              <CommonTextField
                id='demo-debounced'
                label='Debounced (400ms)'
                value={debouncedValue}
                onChange={setDebouncedValue}
                debounce={true}
                debounceTime={400}
                helperText='Input is debounced'
              />
            }
          />
          <DemoComponent
            label='Numbers Only'
            component={
              <CommonTextField
                id='demo-restricted'
                label='Numbers Only'
                value={restrictedValue}
                onChange={setRestrictedValue}
                restrictedValues={/^[0-9]*$/}
                helperText='Only numbers allowed'
              />
            }
          />
          <DemoComponent
            label='With Loader'
            component={
              <CommonTextField
                id='demo-loader'
                label='With Loader'
                value={loadingValue}
                onChange={setLoadingValue}
                loading={true}
              />
            }
          />
        </StyledDemoGrid>
      </SubSection>

      {/* Text Field States */}
      <SubSection
        title='Text Field States'
        description='Different states: enabled, disabled, readonly, and with helper text'
      >
        <StyledSizeLabel variant='h6' gutterBottom>
          Component States
        </StyledSizeLabel>
        <StyledDemoGrid>
          <DemoComponent
            label='Enabled'
            component={
              <CommonTextField
                id='demo-enabled'
                label='Enabled Field'
                value={enabledValue}
                onChange={setEnabledValue}
              />
            }
          />
          <DemoComponent
            label='Disabled'
            component={
              <CommonTextField
                id='demo-disabled'
                label='Disabled Field'
                value='Disabled content'
                onChange={() => {}}
                disabled={true}
              />
            }
          />
          <DemoComponent
            label='Read-only'
            component={
              <CommonTextField
                id='demo-readonly'
                label='Read-only Field'
                value={readonlyValue}
                onChange={setReadonlyValue}
                readonly={true}
              />
            }
          />
          <DemoComponent
            label='With Helper Text'
            component={
              <CommonTextField
                id='demo-helper'
                label='With Helper'
                value={helperValue}
                onChange={setHelperValue}
                helperText='This is helpful information'
              />
            }
          />
        </StyledDemoGrid>
      </SubSection>

      {/* Size Options */}
      <SubSection
        title='Size Options'
        description='Different width configurations and input sizes - shown across width and height variations'
      >
        {/* Small Sizes */}
        <StyledSizeLabel variant='h6' gutterBottom>
          Small
        </StyledSizeLabel>
        <StyledDemoGrid>
          <DemoComponent
            label='Small Input'
            component={
              <CommonTextField
                id='demo-size-small'
                label='Small Input'
                value={smallValue}
                onChange={setSmallValue}
                size='small'
                width='180px'
                fullWidth={false}
              />
            }
          />
          <DemoComponent
            label='Small Multiline'
            component={
              <CommonTextField
                id='demo-size-small-multi'
                label='Small Multiline'
                value={smallMultiValue}
                onChange={setSmallMultiValue}
                size='small'
                multiline
                rows={2}
                width='180px'
                fullWidth={false}
              />
            }
          />
        </StyledDemoGrid>

        {/* Medium Sizes */}
        <StyledSizeLabel variant='h6' gutterBottom>
          Medium
        </StyledSizeLabel>
        <StyledDemoGrid>
          <DemoComponent
            label='Default Input'
            component={
              <CommonTextField
                id='demo-size-medium'
                label='Default Input'
                value={mediumValue}
                onChange={setMediumValue}
                width='240px'
                fullWidth={false}
              />
            }
          />
          <DemoComponent
            label='Medium Multiline'
            component={
              <CommonTextField
                id='demo-size-medium-multi'
                label='Medium Multiline'
                value={mediumMultiValue}
                onChange={setMediumMultiValue}
                multiline
                rows={3}
                width='240px'
                fullWidth={false}
              />
            }
          />
        </StyledDemoGrid>
      </SubSection>

      {/* Input Types */}
      <SubSection
        title='Input Types'
        description='Different input types for various data formats'
      >
        <StyledSizeLabel variant='h6' gutterBottom>
          Specialized Inputs
        </StyledSizeLabel>
        <StyledDemoGrid>
          <DemoComponent
            label='Email'
            component={
              <CommonTextField
                id='demo-email'
                label='Email Address'
                value={emailValue}
                onChange={setEmailValue}
                type='email'
                placeholder='user@example.com'
                width='220px'
                fullWidth={false}
              />
            }
          />
          <DemoComponent
            label='Password'
            component={
              <CommonTextField
                id='demo-password'
                label='Password'
                value={passwordValue}
                onChange={setPasswordValue}
                type='password'
                width='200px'
                fullWidth={false}
              />
            }
          />
          <DemoComponent
            label='Number'
            component={
              <CommonTextField
                id='demo-number'
                label='Number Input'
                value={numberValue}
                onChange={setNumberValue}
                type='number'
                width='200px'
                fullWidth={false}
              />
            }
          />
        </StyledDemoGrid>
      </SubSection>

      {/* Textarea Component */}
      <SubSection
        title='Textarea Component'
        description='Multi-line text input areas with enhanced functionality'
      >
        <StyledSizeLabel variant='h6' gutterBottom>
          Basic Textarea
        </StyledSizeLabel>
        <StyledDemoGrid>
          <DemoComponent
            label='Small Textarea'
            component={
              <CommonTextarea
                id='demo-textarea-small'
                label='Small Textarea'
                value={textareaValue}
                setValue={setTextareaValue}
                minRows={2}
                width='250px'
                fullWidth={false}
              />
            }
          />
          <DemoComponent
            label='Standard Textarea'
            component={
              <CommonTextarea
                id='demo-textarea-standard'
                label='Standard Textarea'
                value={textareaValue}
                setValue={setTextareaValue}
                minRows={4}
                width='300px'
                fullWidth={false}
              />
            }
          />
          <DemoComponent
            label='Large Textarea'
            component={
              <CommonTextarea
                id='demo-textarea-large'
                label='Large Textarea'
                value={textareaValue}
                setValue={setTextareaValue}
                minRows={6}
                width='350px'
                fullWidth={false}
              />
            }
          />
        </StyledDemoGrid>

        <StyledSizeLabel variant='h6' gutterBottom>
          Textarea Features
        </StyledSizeLabel>
        <StyledDemoGrid>
          <DemoComponent
            label='Required Textarea'
            component={
              <CommonTextarea
                id='demo-textarea-required'
                label='Required Textarea'
                value={textareaRequired}
                setValue={setTextareaRequired}
                required={true}
                helperText='This field is required'
                minRows={3}
                width='280px'
                fullWidth={false}
              />
            }
          />
          <DemoComponent
            label='With Error'
            component={
              <CommonTextarea
                id='demo-textarea-error'
                label='Textarea with Error'
                value={textareaError}
                setValue={setTextareaError}
                error='Please enter valid content'
                minRows={3}
                width='280px'
                fullWidth={false}
              />
            }
          />
          <DemoComponent
            label='Debounced Textarea'
            component={
              <CommonTextarea
                id='demo-textarea-debounced'
                label='Debounced (400ms)'
                value={textareaDebounced}
                setValue={setTextareaDebounced}
                debounce={true}
                debounceTime={400}
                helperText='Input is debounced'
                minRows={3}
                width='280px'
                fullWidth={false}
              />
            }
          />
          <DemoComponent
            label='Character Limited'
            component={
              <CommonTextarea
                id='demo-textarea-restricted'
                label='Max 100 Characters'
                value={textareaRestricted}
                setValue={setTextareaRestricted}
                restrictedValues={/^.{0,100}$/}
                helperText={`${textareaRestricted.length}/100 characters`}
                minRows={3}
                width='280px'
                fullWidth={false}
              />
            }
          />
        </StyledDemoGrid>

        <StyledSizeLabel variant='h6' gutterBottom>
          Textarea States
        </StyledSizeLabel>
        <StyledDemoGrid>
          <DemoComponent
            label='Enabled'
            component={
              <CommonTextarea
                id='demo-textarea-enabled'
                label='Enabled Textarea'
                value={textareaValue}
                setValue={setTextareaValue}
                minRows={3}
                width='250px'
                fullWidth={false}
              />
            }
          />
          <DemoComponent
            label='Disabled'
            component={
              <CommonTextarea
                id='demo-textarea-disabled'
                label='Disabled Textarea'
                value='This textarea is disabled and cannot be edited.'
                setValue={() => {}}
                disabled={true}
                minRows={3}
                width='250px'
                fullWidth={false}
              />
            }
          />
          <DemoComponent
            label='With Helper Text'
            component={
              <CommonTextarea
                id='demo-textarea-helper'
                label='With Helper Text'
                value={textareaValue}
                setValue={setTextareaValue}
                helperText='Enter your detailed feedback here'
                minRows={3}
                width='250px'
                fullWidth={false}
              />
            }
          />
        </StyledDemoGrid>
      </SubSection>
    </>
  )
}
